'use client'

import * as React from 'react'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { FadeIn } from '../motion/FadeIn'

interface BlogPost {
  title: string
  excerpt: string
  date: string
  href: string
  image?: string
  author?: {
    name: string
    avatar?: string
  }
}

interface BlogPageProps {
  heading?: string
  subheading?: string
  posts?: BlogPost[]
  emptyState?: {
    message: string
    cta?: {
      text: string
      href: string
    }
  }
  icon?: React.ReactNode
}

export function BlogPage({
  heading = 'Blog',
  subheading = 'Tips, tutorials, and product updates.',
  posts = [],
  emptyState = {
    message: 'Tips, tutorials, and product updates coming soon.',
  },
  icon,
}: BlogPageProps) {
  const isEmpty = posts.length === 0

  return (
    <Section className="min-h-[60vh]">
      <Container size="md">
        {isEmpty ? (
          <div className="text-center">
            {icon && (
              <FadeIn>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--accent)]/10 mb-6">
                  {icon}
                </div>
              </FadeIn>
            )}
            <FadeIn delay={0.1}>
              <h1 className="headline-lg mb-4">{heading}</h1>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="body-lg mb-8">{emptyState.message}</p>
            </FadeIn>
            {emptyState.cta && (
              <FadeIn delay={0.3}>
                <p className="text-[var(--landing-text-tertiary)]">
                  <a href={emptyState.cta.href} className="text-[var(--accent)] hover:underline">
                    {emptyState.cta.text}
                  </a>
                </p>
              </FadeIn>
            )}
          </div>
        ) : (
          <>
            <FadeIn>
              <h1 className="headline-lg mb-4">{heading}</h1>
            </FadeIn>
            {subheading && (
              <FadeIn delay={0.1}>
                <p className="body-lg mb-12">{subheading}</p>
              </FadeIn>
            )}
            <div className="grid gap-8 md:grid-cols-2">
              {posts.map((post, index) => (
                <FadeIn key={post.href} delay={0.1 + index * 0.05}>
                  <a
                    href={post.href}
                    className="block group landing-card p-6 hover:border-[var(--accent)] transition-colors"
                  >
                    {post.image && (
                      <div className="aspect-video rounded-lg overflow-hidden mb-4 bg-[var(--landing-bg-muted)]">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <p className="text-sm text-[var(--landing-text-tertiary)] mb-2">{post.date}</p>
                    <h2 className="text-xl font-semibold text-[var(--landing-text-primary)] mb-2 group-hover:text-[var(--accent)] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-[var(--landing-text-secondary)]">{post.excerpt}</p>
                    {post.author && (
                      <div className="flex items-center gap-2 mt-4">
                        {post.author.avatar && (
                          <img
                            src={post.author.avatar}
                            alt={post.author.name}
                            className="w-6 h-6 rounded-full"
                          />
                        )}
                        <span className="text-sm text-[var(--landing-text-tertiary)]">
                          {post.author.name}
                        </span>
                      </div>
                    )}
                  </a>
                </FadeIn>
              ))}
            </div>
          </>
        )}
      </Container>
    </Section>
  )
}
